<script setup lang="ts">
import {
  inject,
  ref,
  watchEffect,
} from 'vue';
import {
  CdrLink,
  CdrText,
} from '@rei/cedar';
import { useIcons } from '../../../../common/composables/useIcons';
import TopToolsComponent from '../topTools/TopToolsComponent.vue';
import { apiCall } from '../../../../api/services/apiCalls';
import CollapsedCategory from './CollapsedCategory.vue';
import { THelpCenterCategory } from '../../models/types/HelpCenterCategory';
import { ExpandedCategoryProps } from '../../models/interfaces/ExpandedCategoryProps';
import { centerState } from '../../models/types/ColumnsCenterState';
import externalArticles from '../../models/constants/ExternalArticles';

import tags from '../../../../utils/globalTags';
import { camelCase, snakeCase } from '../../../../common/utils';
import { sendClickAnalytics } from '../../../../utils';

const { helpCenter } = defineProps<ExpandedCategoryProps>();
const { setState, mainState } = inject<centerState>('centerState');

const activeSection = ref('');
const centerSections = ref([]);
const prevSections = ref([]);
const sectionLinks = ref([]);
const backButton = {
  id: '', header: 'Go back', isBackButton: true,
};

const categoriesMetricHandler = (categoryValue: string, isNested: boolean) => {
  let linkName = `${tags.HELP_CENTER_UPPERCASE}_${snakeCase(mainState.value.helpCenter || '')}`;
  const categoryValueParsed = camelCase(categoryValue);
  if (isNested) {
    linkName += `_${categoryValueParsed}`;
    setState({ secondaryCategory: categoryValueParsed });
  } else if (isNested === undefined && !mainState.value.secondaryCategory) {
    linkName += `_${categoryValueParsed}`;
  } else {
    linkName += `_${mainState.value.secondaryCategory}_${categoryValueParsed}`;
  }
  const clickData = {
    pageName: '',
    linkName,
  };
  setState({ adobeName: linkName });
  sendClickAnalytics(clickData);
};

const isExternal = (articleId: string) => Object.prototype.hasOwnProperty.call(externalArticles, articleId);

const handleRouting = (id: string) => {
  if (!isExternal(id)) {
    setState({ articleId: id });
  } else {
    globalThis.location.href = externalArticles[id].target;
  }
};

const resetState = () => {
  if (mainState.value.helpCenter !== undefined && mainState.value.secondaryCategory !== undefined) {
    setState({ secondaryCategory: '' });
  } else {
    setState({ helpCenter: '' });
  }
};

const setSectionLinks = (center: THelpCenterCategory) => {
  const {
    header, answers, isNested, isBackButton,
  } = center;
  const isCollapsingClick = header === activeSection.value;
  switch (true) {
    case isNested !== undefined && isNested:
      prevSections.value = centerSections.value;
      backButton.header = header;
      centerSections.value = [backButton, ...answers];
      categoriesMetricHandler(center.header, isNested);
      break;
    case isBackButton !== undefined && isBackButton:
      centerSections.value = prevSections.value;
      prevSections.value = [];
      resetState();
      break;
    default:
      activeSection.value = isCollapsingClick ? '' : header;
      sectionLinks.value = isCollapsingClick ? [] : answers;
      categoriesMetricHandler(center.header, isNested);
      break;
  }
};

const getSubsections = async () => {
  const helpCenters = await apiCall('default', 'help-topics', 'true', 'includeDetails');
  // SACS-3516: Defensive check for nested section destructuring
  if (Array.isArray(helpCenters) && helpCenters.length > 0) {
    const nestedSection = helpCenters.filter((section: { children: any[]; }) => section.children && section.children.length);
    if (nestedSection.length > 0 && nestedSection[0]?.children) {
      const { children } = nestedSection[0];
      const transformed = children.reduce((acc: [{ [key: string]: any }], curr: { [key: string]: any }) => {
        const { helpCenterId: id, helpCenterName: header, sections } = curr;
        return [...acc, {
          id,
          header,
          answers: [...sections],
          isNested: true,
        }];
      }, []);
      centerSections.value = [...transformed];
      sectionLinks.value = [];
      return;
    }
  }

  // Fallback if API fails or returns invalid structure
  centerSections.value = [];
  sectionLinks.value = [];
};

watchEffect(() => {
  // IIFE to fix the Promise returned in function argument where a void return was expected sonar error.
  (async () => {
    const { helpCenterId } = helpCenter;
    if (helpCenter && helpCenterId === '') {
      getSubsections();
      return;
    }
    if (!helpCenterId) return;
    const response = await apiCall('default', `help-topics/helpCenterIds/${helpCenterId}`, 'true', 'includeDetails');
    // SACS-3516: Defensive check to prevent destructuring undefined if API returns an Error or empty array
    if (Array.isArray(response) && response.length > 0 && response[0]?.sections) {
      centerSections.value = [...response[0].sections];
      sectionLinks.value = [];
    } else {
      centerSections.value = [];
      sectionLinks.value = [];
    }
  })();
});
</script>

<template>
  <div class="help-centers-u__expanded-category">
    <div v-if="!prevSections.length">
      <div
        :key="helpCenter.helpCenterName"
        :data-ui="helpCenter.helpCenterName"
        class="help-centers-u__card help-centers-u__card--active"
        tag="button"
        tabindex="0"
        @click="handleClick(helpCenter)"
      >
        <div class="help-centers__caret-div">
          <component
            :is="useIcons('IconCaretLeft')"
            class="help-centers-u__icon help-centers-u__icon--caret"
          />
        </div>
        <div class="help-centers-u__card-content help-centers-u__card-content--active">
          <div>
            <component
              :is="useIcons(helpCenter.helpCenterIcon || 'IconReturns')"
              class="help-centers-u__icon"
            />
          </div>
          <CdrText
            tag="h1"
            class="help-centers-u__card-header"
          >
            {{ helpCenter.helpCenterName }}
          </CdrText>
        </div>
      </div>
      <TopToolsComponent
        :active-section="helpCenter.conmanKey"
      />
    </div>
    <div
      v-for="(center) in centerSections"
      :key="center.id || center.header"
      role="button"
    >
      <CollapsedCategory
        :helpCenterCategory="center"
        @click="setSectionLinks(center)"
      />
      <div
        class="help-centers-u__card-list"
        v-if="center.header === activeSection"
      >
        <ul
          :key="link.name"
          v-for="(link) in sectionLinks"
        >
          <li class="help-centers-u__card-link">
            <CdrLink
              @click.prevent="handleRouting(link.id)"
            >
              {{ link.name }}
              <component
                v-if="isExternal(link.id)"
                :is="useIcons('IconExternalLink')"
                class="help-centers-u__icon help-centers-u__icon--external"
              />
            </CdrLink>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  @import 'src/main/style/helpCenterColumnsUpdate.scss';
</style>
